import { useEffect, useRef, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/primitives/button";
import {
  IMPLEMENTED_SETTINGS_CATEGORIES,
  SettingsCategory,
  useNavigationState,
} from "@/zustand/NavigationState";
import { cn } from "@/lib/utils";
import ProfileContent from "@/components/chat/left-column/menu-views/settings/profile/ProfileContent";
import SecurityContent from "@/components/chat/left-column/menu-views/settings/account/SecurityContent";
import AppearanceContent from "@/components/chat/left-column/menu-views/settings/appearance/AppearanceContent";
import LanguageContent from "@/components/chat/left-column/menu-views/settings/language/LanguageContent";
import PreferencesContent from "@/components/chat/left-column/menu-views/settings/preferences/PreferencesContent";
import { CATEGORY_LABEL_KEY } from "@/components/chat/left-column/menu-views/settings/categoryLabels";

/**
 * The middle-column view for one settings category (flag
 * `settingsMiddleViews`; docs/settings-middle-views/design.md).
 *
 * One shell for all categories: a single mount point keeps a future
 * pane-to-pane animation possible without touching NavigationState.
 *
 * Chrome: the back chevron renders in BOTH width branches — Tailwind `lg` is
 * 960px while the compact cutoff is 700px, and in the 700–959px band the view
 * still covers the left column and the bottom nav, so a breadcrumb-only header
 * would strand the user (the payments views shipped exactly this dead band;
 * fixed on the same branch).
 */

// The accordion's AccordionContent carried these layout classes per category;
// the shell owns them now so the content components stay layout-neutral.
const CATEGORY_LAYOUT: Record<SettingsCategory, string> = {
  profile: "flex flex-col gap-4",
  security: "flex flex-col gap-8",
  appearance: "flex flex-col gap-1",
  language: "flex flex-col gap-1",
  preferences: "flex flex-col gap-1",
};

function CategoryBody({ category }: { category: SettingsCategory }) {
  switch (category) {
    case "profile":
      return <ProfileContent />;
    case "security":
      return <SecurityContent />;
    case "appearance":
      return <AppearanceContent />;
    case "language":
      return <LanguageContent />;
    case "preferences":
      return <PreferencesContent />;
    default:
      return null;
  }
}

export default function SettingsCategoryView() {
  const { t } = useTranslation();
  const category = useNavigationState((s) => s.activeSettingsCategory);
  const cancelSettingsCategoryView = useNavigationState(
    (s) => s.cancelSettingsCategoryView,
  );

  const [containerWidth, setContainerWidth] = useState(1200);
  const isCompact = containerWidth < 700;
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = wrapRef.current?.parentElement ?? wrapRef.current;
    if (!target) {
      return;
    }
    const measure = () =>
      setContainerWidth(target.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(target);
    return () => ro.disconnect();
  }, []);

  // On mobile the view covers the entire screen (including the bottom nav);
  // move focus into it so keyboard/AT users aren't left on a covered row.
  useEffect(() => {
    wrapRef.current?.focus();
  }, [category]);

  if (!category || !IMPLEMENTED_SETTINGS_CATEGORIES.includes(category)) {
    return null;
  }

  const label = t(CATEGORY_LABEL_KEY[category]);

  return (
    <div
      ref={wrapRef}
      tabIndex={-1}
      className="absolute inset-0 flex size-full flex-col overflow-hidden bg-background outline-none lg:border-l lg:border-border"
    >
      {isCompact ? (
        <div className="grid grid-cols-[32px_1fr_32px] items-center gap-2 border-b border-border bg-card/60 px-3.5 py-2.5">
          <Button
            size="icon"
            variant="ghost"
            aria-label="back"
            onClick={cancelSettingsCategoryView}
            className="size-8"
          >
            <ChevronLeft size={18} />
          </Button>
          <span className="text-center text-[14px] font-semibold">{label}</span>
          <span />
        </div>
      ) : (
        <div className="flex h-leftColumnHeaderHeight items-center gap-2 border-b border-border px-3.5 lg:px-5">
          {/* Visible only below lg (960px), where the view covers the left
              column — incl. the 700-959px breadcrumb band. At lg+ the left
              column is beside the view and back would only produce the empty
              state. */}
          <Button
            size="icon"
            variant="ghost"
            aria-label="back"
            onClick={cancelSettingsCategoryView}
            className="size-8 lg:hidden"
          >
            <ChevronLeft size={18} />
          </Button>
          <span className="text-sm text-muted-foreground">
            {t("settings")} / {label}
          </span>
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-4 py-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <div
          className={cn(
            "mx-auto w-full max-w-2xl text-sm",
            CATEGORY_LAYOUT[category],
          )}
        >
          <CategoryBody category={category} />
        </div>
      </div>
    </div>
  );
}
